import { useState } from "react";
import { toast } from "sonner";
import { Download, Share, Smartphone, X } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import usePWAInstall, { isIosDevice, isDesktopSafari, isChromiumInstallCapable } from "@/hooks/usePWAInstall";

/**
 * Which of the four mutually-exclusive states this strip is in. The
 * non-"install" ones only ever apply under `alwaysShow` (Landing and
 * Dashboard): every other route keeps the original narrower footprint —
 * appearing only when there's a live prompt to offer.
 *
 *  - "install": any Chromium browser (`isChromiumInstallCapable`), whether or
 *    not a prompt has actually fired on this exact page load yet. Under
 *    `alwaysShow` this deliberately does NOT wait for `canInstall` to flip
 *    true first — a Chromium browser with no live prompt right now is most
 *    often "already installed" (Chrome won't re-offer one — the giveaway is
 *    its own "Open in app" omnibox chip, which this component has no way to
 *    read) rather than "can't install", and hiding in that case is what
 *    made the strip disappear on Chrome and look broken. The button itself
 *    stays honest either way: `onInstallClick` below checks the real
 *    outcome and says so if there's genuinely nothing to prompt with.
 *  - "ios": Safari on iOS/iPadOS — Add-to-Home-Screen via the Share sheet.
 *  - "safariDesktop": Safari on macOS — Add to Dock via the File/Share menu.
 *  - "unsupported": everything else with zero install path at all — most
 *    notably Firefox (desktop and Android), which has no
 *    `beforeinstallprompt` and, per Mozilla's own stated position, isn't
 *    planning to add one.
 */
function installMode({ canInstall, isInstalled, alwaysShow }) {
  if (isInstalled) return null;
  if (canInstall) return "install";
  if (!alwaysShow) return null;
  if (isChromiumInstallCapable()) return "install";
  if (isIosDevice()) return "ios";
  if (isDesktopSafari()) return "safariDesktop";
  return "unsupported";
}

/**
 * Slim install strip — one row, sits directly under the header on both the
 * landing page and the dashboard (both pass `alwaysShow`, see below). See
 * `installMode` above for what each platform sees.
 *
 * @param {boolean} alwaysShow
 *   Default (unused by Landing/Dashboard today, kept for any future route
 *   that wants the narrower footprint): closing the strip writes the shared
 *   session-level dismissal in usePWAInstall, so it stays gone for the rest
 *   of the browser session and every other install surface goes quiet with
 *   it. Only ever renders in "install" mode (see above) — silent otherwise,
 *   rather than risking a false claim on a route nobody's watching closely.
 *
 *   With `alwaysShow` (Landing, Dashboard): the strip ignores that shared
 *   dismissal entirely and its close button only hides it for the current
 *   visit — held in component state, which resets on the next mount. So
 *   every arrival shows it again, first-time or returning, while the
 *   visitor can still push it out of the way for the page they're on. Also
 *   turns on the iOS/desktop-Safari/unsupported modes, so every browser
 *   sees *something honest* — actionable where the platform allows it,
 *   plainly not-yet-possible where it doesn't — rather than Chrome being
 *   the only browser this strip ever appears in.
 */
export default function InstallBanner({ alwaysShow = false }) {
  const { t } = useI18n();
  const { canInstall, isInstalled, isDismissed, install, dismiss } = usePWAInstall();
  const [closedThisVisit, setClosedThisVisit] = useState(false);

  const mode = installMode({ canInstall, isInstalled, alwaysShow });
  const hidden = alwaysShow ? closedThisVisit : isDismissed;
  const onClose = alwaysShow ? () => setClosedThisVisit(true) : dismiss;

  // "install" mode shows this button even when canInstall is currently
  // false (see installMode's comment) — most often because it's already
  // installed. `install()` already resolves 'unavailable' safely rather
  // than throwing when there's no real prompt to open; this is the one
  // extra step of actually telling the visitor that instead of the click
  // silently doing nothing, which would just look broken.
  const onInstallClick = async () => {
    const outcome = await install();
    if (outcome === "unavailable") toast.message(t("install.already_installed_hint"));
  };

  if (!mode || hidden) return null;

  return (
    <div className="max-w-[1280px] mx-auto w-full px-5 sm:px-8 pt-5" data-testid="install-banner-wrap">
      <div
        className="install-reveal flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl border border-[#D1FAE5] dark:border-[#22453A] bg-gradient-to-r from-[#ECFDF5] via-[#F4FBF7] to-white dark:from-[#173D31] dark:via-[#14332B] dark:to-[#12221D] px-4 sm:px-5 py-4"
        role="region"
        aria-labelledby="install-banner-title"
        data-testid="install-banner">
        <div
          className="w-11 h-11 shrink-0 rounded-xl bg-[#064E3B] text-[#FED7AA] grid place-items-center"
          aria-hidden="true">
          {mode === "ios" || mode === "safariDesktop" ? <Share className="w-5 h-5" /> : <Smartphone className="w-5 h-5" />}
        </div>

        <div className="flex-1 min-w-[11rem]">
          <div
            id="install-banner-title"
            className="font-heading font-semibold text-[#064E3B] dark:text-[#F3EFE4] leading-tight">
            {t("install.download_title")}
          </div>
          <div className="text-sm text-[#4B5563] dark:text-[#B7C2BB] mt-0.5">
            {mode === "ios" && (
              <>
                {t("install.ios_step_pre")}{" "}
                <span className="font-medium text-[#1F2937] dark:text-[#F3EFE4]">{t("install.ios_share")}</span>
                {" → "}
                <span className="font-medium text-[#1F2937] dark:text-[#F3EFE4]">{t("install.ios_add_to_home")}</span>{" "}
                {t("install.ios_step_post")}
              </>
            )}
            {mode === "safariDesktop" && t("install.safari_desktop_instructions")}
            {mode === "unsupported" && t("install.unsupported_browser")}
            {mode === "install" && t("install.banner_subtitle")}
          </div>
        </div>

        <div className="flex items-center gap-1 ms-auto">
          {mode === "install" && (
            <button
              type="button"
              onClick={onInstallClick}
              data-testid="install-banner-button"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] text-white text-sm font-medium transition hover:bg-[#047857] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] focus-visible:ring-offset-2 focus-visible:ring-offset-[#ECFDF5] dark:focus-visible:ring-offset-[#173D31]">
              <Download className="w-4 h-4" aria-hidden="true" />
              {t("install.install_app")}
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            aria-label={t("install.dismiss")}
            data-testid="install-banner-dismiss"
            className="w-9 h-9 grid place-items-center rounded-full text-[#4B5563] dark:text-[#B7C2BB] transition hover:bg-white/70 dark:hover:bg-[#16302A] hover:text-[#1F2937] dark:hover:text-[#F3EFE4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706]">
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
